import React, { useEffect, useMemo, useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { setModalState, modalViewsConst } from '../../store/modalSlice';
import { updateSchedule, updateRunner, addRunner, removeRunner } from '../../store/agentScheduleSlice';
import ModalBase from './ModalBase';
import ScheduleQuestionEditor, { ScheduleQuestionValue, isEmailConditionInvalid } from './schedule/ScheduleQuestionEditor';
import ScheduleFrequencyOptions from './schedule/ScheduleFrequencyOptions';
import { AttachedFile } from '../../services/mcpService';
import { ShareService } from '../../services/shareService';
import { buildAssetContext, stripAssetContext } from '../../utils/assetContext';
import { usePostHogCapture } from '../../hooks/usePostHogCapture';
import { POSTHOG_EVENTS } from '../../utils/posthogEvents';
import { FrequencyType, parseRuntime, buildRuntime, buildCronExpression, computeNextRunFromCron, isValidCron } from '../../utils/scheduleFrequency';
import type { AgentSchedule } from '../../services/scheduleService';
import type { AppDispatch, RootState } from '../../store';

interface EditScheduleModalProps {
  onClose: (result?: any) => void;
  schedule: AgentSchedule;
}

// Parse a runner's stored attachments (AWSJSON string) into the editor's array shape.
const parseAttachments = (raw?: string | null): AttachedFile[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const serializeAttachments = (files?: AttachedFile[]): string | undefined =>
  files && files.length ? JSON.stringify(files) : undefined;

const EditScheduleModal: React.FC<EditScheduleModalProps> = ({ onClose, schedule }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { capture } = usePostHogCapture();
  // Assets-by-group dict so a mentioned group's member assets are listed in the baked context.
  const dataGroups = useSelector(
    (state: RootState) => state.assets.dataGroups as Record<string, { name: string }[]>
  );

  const parsed = parseRuntime(schedule.runtime);
  const [freqType, setFreqType] = useState<FrequencyType>(parsed.type);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [ampm, setAmpm] = useState<'AM' | 'PM'>(parsed.ampm);
  const [weekDay, setWeekDay] = useState(parsed.weekDay);
  const [monthDay, setMonthDay] = useState(parsed.monthDay);
  // Custom schedules re-hydrate their raw cron from the stored runtime; fall back to the persisted column.
  const [cronExpression, setCronExpression] = useState(parsed.cronExpression || schedule.cronExpression || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);

  const runners = schedule.runners?.items || [];
  const [questions, setQuestions] = useState<ScheduleQuestionValue[]>(
    runners.map((r) => ({
      id: r.id,
      title: r.title,
      // Strip any baked asset-context block so the editor shows the clean prompt (+ @tokens).
      prompt: stripAssetContext(r.prompt),
      attachments: parseAttachments(r.attachments),
      mentionedAssets: [],
      sendEmail: r.sendEmail ?? false,
      // null mode with a condition = legacy/default "gate"; no condition = "always".
      emailMode: r.emailCondition ? (r.emailConditionMode === 'notify' ? 'notify' : 'gate') : 'always',
      emailCondition: r.emailCondition ?? '',
    }))
  );

  // Live share links on tasks the user has removed from the list (pending delete on Save). Deleting a runner doesn't kill its share links automatically; the checkbox lets the user cascade-deactivate them (default on). Counts are fetched once per removed runner.
  const [runnerShareCounts, setRunnerShareCounts] = useState<Record<string, number>>({});
  const [deactivateShares, setDeactivateShares] = useState(true);

  const removedRunnerIds = useMemo(
    () => runners.filter((r) => !questions.some((q) => q.id === r.id)).map((r) => r.id),
    [runners, questions]
  );

  useEffect(() => {
    const missing = removedRunnerIds.filter((id) => runnerShareCounts[id] === undefined);
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (id) => [id, (await ShareService.listActiveShareIdsForRunner(id)).length] as const)
      );
      if (!cancelled) setRunnerShareCounts((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => {
      cancelled = true;
    };
  }, [removedRunnerIds, runnerShareCounts]);

  const removedShareCount = removedRunnerIds.reduce(
    (sum, id) => sum + (runnerShareCounts[id] ?? 0),
    0
  );

  const goBackToManage = () => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.manageSchedules,
      properties: { __modalSize: 'md' },
    }));
  };

  const handleClose = () => goBackToManage();

  const handleSave = async () => {
    const validQuestions = questions.filter((q) => q.prompt.trim());
    if (validQuestions.length === 0 || isSaving) return;
    if (freqType === 'Custom' && !isValidCron(cronExpression)) return;

    setIsSaving(true);
    try {
      // Update schedule if runtime changed — must also update cronExpression and nextRunAt, otherwise the poller keeps firing on the old schedule.
      const newRuntime = buildRuntime(freqType, hour, minute, ampm, weekDay, monthDay, cronExpression);

      if (newRuntime !== schedule.runtime) {
        const newCronExpression = buildCronExpression(freqType, hour, minute, ampm, weekDay, monthDay, cronExpression);
        const newNextRunAt = computeNextRunFromCron(newCronExpression, schedule.timezone);
        await dispatch(updateSchedule({
          id: schedule.id,
          runtime: newRuntime,
          cronExpression: newCronExpression,
          nextRunAt: newNextRunAt,
        })).unwrap();
      }

      // Update/create/delete runners
      const existingIds = new Set(runners.map((r) => r.id));
      const keptIds = new Set<string>();

      for (let i = 0; i < validQuestions.length; i++) {
        const q = validQuestions[i];
        const attachments = serializeAttachments(q.attachments);
        // Re-bake @-mention asset context into the prompt (q.prompt is the clean text + @tokens).
        const prompt = stripAssetContext(q.prompt).trim() + buildAssetContext(q.mentionedAssets ?? [], dataGroups);
        if (q.id && existingIds.has(q.id)) {
          // Update existing runner
          keptIds.add(q.id);
          const existing = runners.find((r) => r.id === q.id);
          const attachmentsChanged = (existing?.attachments || undefined) !== attachments;
          const sendEmailChanged = Boolean(existing?.sendEmail) !== Boolean(q.sendEmail);
          // Same sendEmail-off drop as CreateScheduleModal; null (not undefined) so clearing the textarea (or switching to Always) actually erases the stored fields.
          const conditionActive = (q.sendEmail ?? false) && (q.emailMode === 'gate' || q.emailMode === 'notify');
          const emailCondition = conditionActive ? q.emailCondition?.trim() || null : null;
          const emailConditionMode = conditionActive && q.emailMode === 'notify' ? 'notify' : null;
          const emailConditionChanged = (existing?.emailCondition ?? null) !== emailCondition;
          const emailConditionModeChanged = (existing?.emailConditionMode ?? null) !== emailConditionMode;
          if (existing && (existing.title !== q.title || existing.prompt !== prompt || existing.sortOrder !== i || attachmentsChanged || sendEmailChanged || emailConditionChanged || emailConditionModeChanged)) {
            await dispatch(updateRunner({ id: q.id, title: q.title, prompt, attachments, sortOrder: i, sendEmail: q.sendEmail ?? false, emailCondition, emailConditionMode })).unwrap();
          }
        } else {
          // Create new runner — pass the pre-generated id so it matches the upload prefix
          await dispatch(addRunner({
            id: q.id,
            scheduleId: schedule.id,
            title: q.title,
            prompt,
            attachments,
            sortOrder: i,
            sendEmail: q.sendEmail ?? false,
            emailCondition: q.sendEmail && (q.emailMode === 'gate' || q.emailMode === 'notify')
              ? q.emailCondition?.trim() || undefined
              : undefined,
            emailConditionMode: q.sendEmail && q.emailMode === 'notify' ? 'notify' : undefined,
          })).unwrap();
        }
      }

      // Remove runners that were deleted
      for (const runner of runners) {
        if (!keptIds.has(runner.id)) {
          await dispatch(removeRunner({ runnerId: runner.id, scheduleId: schedule.id, deactivateShares })).unwrap();
        }
      }

      const conditionCount = validQuestions.filter((q) => q.sendEmail && (q.emailMode === 'gate' || q.emailMode === 'notify') && q.emailCondition?.trim()).length;
      if (conditionCount > 0) {
        capture(POSTHOG_EVENTS.SCHEDULE_EMAIL_CONDITION_SET, {
          questionCount: validQuestions.length,
          conditionCount,
        });
      }

      goBackToManage();
    } catch (err) {
      console.error('[EditScheduleModal] Error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const updateQuestion = (index: number, patch: Partial<ScheduleQuestionValue>) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, { id: crypto.randomUUID(), title: '', prompt: '', attachments: [], mentionedAssets: [], sendEmail: false, emailMode: 'always', emailCondition: '' }]);
  };

  const removeQuestion = (index: number) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const isCronInvalid = freqType === 'Custom' && !isValidCron(cronExpression);
  // Blank-prompt questions are dropped at submit, so they must not hold Save hostage.
  const hasInvalidEmailCondition = questions.some((q) => q.prompt.trim() && isEmailConditionInvalid(q));
  const isSaveDisabled = questions.every((q) => !q.prompt.trim()) || isCronInvalid || hasInvalidEmailCondition || isSaving;

  return (
    <ModalBase
      title={schedule.title}
      icon={<FiEdit2 className="h-5 w-5" />}
      onClose={handleClose}
      className={`max-[741px]:h-[100dvh] max-[741px]:max-h-[100dvh]${isMapOpen ? ' !hidden' : ''}`}
      footer={
        <>
          <button onClick={handleClose} className="eai-btn-outline">
            Cancel
          </button>
          <button onClick={handleSave} disabled={isSaveDisabled} className="eai-btn-cta">
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-5">

        {/* Frequency + the field each cadence reveals */}
        <ScheduleFrequencyOptions
          freqType={freqType}
          onFreqTypeChange={setFreqType}
          hour={hour}
          onHourChange={setHour}
          minute={minute}
          onMinuteChange={setMinute}
          ampm={ampm}
          onAmpmChange={setAmpm}
          weekDay={weekDay}
          onWeekDayChange={setWeekDay}
          monthDay={monthDay}
          onMonthDayChange={setMonthDay}
          cronExpression={cronExpression}
          onCronExpressionChange={setCronExpression}
          isCronInvalid={isCronInvalid}
        />

        {/* Questions (Runners) */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <span className="eai-meta-label">Tasks</span>
            <button type="button" onClick={addQuestion} className="eai-btn-outline-xs">
              Add task
            </button>
          </div>
          {questions.map((q, i) => (
            <ScheduleQuestionEditor
              key={q.id}
              value={q}
              scheduleId={schedule.id}
              index={i}
              onChange={(patch) => updateQuestion(i, patch)}
              onRemove={questions.length > 1 ? () => removeQuestion(i) : undefined}
              // Shared toggle is safe because only one map drawer can be open at a time (opening one hides the form); make this per-question if questions ever become independently interactive.
              onMapDrawerToggle={setIsMapOpen}
            />
          ))}
        </div>

        {/* Removed tasks with live public share links — cascade-deactivate on Save (opt-out). */}
        {removedShareCount > 0 && (
          <label className="flex cursor-pointer items-start gap-2 rounded-eai-card bg-eai-brand-peach px-3 py-2 font-inter text-eai-body-xs text-eai-ink ring-1 ring-eai-warning-text/25">
            <input
              type="checkbox"
              checked={deactivateShares}
              onChange={(e) => setDeactivateShares(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-eai-cta-primary"
            />
            <span>
              Also turn off {removedShareCount} public share {removedShareCount === 1 ? 'link' : 'links'} for
              the removed {removedRunnerIds.length === 1 ? 'task' : 'tasks'}. If unchecked, the{' '}
              {removedShareCount === 1 ? 'link keeps' : 'links keep'} showing the last result.
            </span>
          </label>
        )}

      </div>
    </ModalBase>
  );
};

export default EditScheduleModal;
